import { useEffect, useRef, useState } from 'react';
import { Avatar, Button, Flex, Input, Text } from '@nula/core-ui';
import { apiFetch, authFetch, ApiError } from '@/lib/http/client';
import { useAuthStore } from '@/stores/use-auth-store';
import styles from './account-tab.module.css';

// Avatar upload limits — must match the API constant in
// apps/nexus/nexus/routes/auth.rs (AVATAR_MAX_BYTES) and the UI helper text.
const AVATAR_MAX_BYTES = 800 * 1024;
const AVATAR_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

interface AccountTabProps {
  user: { name: string; email: string; picture: string } | null;
}

export function AccountTab({ user }: AccountTabProps) {
  const initialFirstName = (user?.name?.split(' ') ?? [''])[0] ?? '';
  const initialLastName = (user?.name?.split(' ') ?? []).slice(1).join(' ') ?? '';

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Controlled name state. Reset whenever the upstream user.name changes
  // (initial load, /auth/me hydration, or another tab updates the store).
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  useEffect(() => {
    setFirstName(initialFirstName);
    setLastName(initialLastName);
  }, [initialFirstName, initialLastName]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const composedName = `${firstName} ${lastName}`.trim().replace(/\s+/g, ' ');
  const originalName = `${initialFirstName} ${initialLastName}`.trim().replace(/\s+/g, ' ');
  const nameDirty = composedName !== originalName;
  const canSave = nameDirty && composedName.length > 0 && !saving;

  const onPickFile = () => {
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file again re-fires onChange.
    e.target.value = '';
    if (!file) return;

    if (!AVATAR_ACCEPTED_TYPES.includes(file.type)) {
      setUploadError('File must be a JPG, PNG, GIF, or WebP image.');
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setUploadError('File is too large. Max size is 800K.');
      return;
    }

    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await authFetch('/auth/me/avatar', { method: 'POST', body: form });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new ApiError(body.code ?? 'UNKNOWN', body.message ?? 'Upload failed', res.status);
      }
      const data = (await res.json()) as { avatarUrl: string };
      // Update auth store so the avatar refreshes everywhere immediately.
      const current = useAuthStore.getState().user;
      if (current) {
        useAuthStore.setState({ user: { ...current, picture: data.avatarUrl } });
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await apiFetch<{ name: string | null }>('/auth/me', {
        method: 'PATCH',
        body: JSON.stringify({ name: composedName }),
      });
      const current = useAuthStore.getState().user;
      if (current) {
        useAuthStore.setState({ user: { ...current, name: updated.name ?? composedName } });
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Flex vertical gap="lg">
      <Flex vertical gap="xs">
        <Text size="md" className={styles.accountSectionLabel}>Account Profile</Text>
        <Text size="sm" type="secondary">Manage your personal information.</Text>
      </Flex>

      {/* Avatar row */}
      <Flex align="center" gap="lg" className={styles.accountAvatarRow}>
        <Avatar src={user?.picture} size={80} shape="circle">
          {!user?.picture && (firstName[0] ?? 'U')}
        </Avatar>
        <Flex vertical gap="xs">
          <Button size="sm" variant="default" onClick={onPickFile} disabled={uploading}>
            {uploading ? 'Uploading…' : 'Upload new picture'}
          </Button>
          <Text size="xs" type="secondary">JPG, GIF or PNG. Max size of 800K</Text>
          {uploadError && <Text size="xs" type="danger">{uploadError}</Text>}
          <input
            ref={fileInputRef}
            type="file"
            accept={AVATAR_ACCEPTED_TYPES.join(',')}
            hidden
            onChange={onFileChange}
          />
        </Flex>
      </Flex>

      {/* Name fields */}
      <Flex vertical gap="sm">
        <Flex gap="sm" className={styles.accountNameRow}>
          <Flex vertical gap="xs" className={styles.accountNameField}>
            <Text size="sm" className={styles.accountSectionLabel}>First Name</Text>
            <Input value={firstName} onChange={e => setFirstName(e.target.value)} size="md" />
          </Flex>
          <Flex vertical gap="xs" className={styles.accountNameField}>
            <Text size="sm" className={styles.accountSectionLabel}>Last Name</Text>
            <Input value={lastName} onChange={e => setLastName(e.target.value)} size="md" />
          </Flex>
        </Flex>
        <Flex vertical gap="xs">
          <Text size="sm" className={styles.accountSectionLabel}>Email</Text>
          <Input
            value={user?.email ?? ''}
            size="md"
            disabled
            title="Email is managed by your sign-in provider"
          />
        </Flex>
        {saveError && <Text size="xs" type="danger">{saveError}</Text>}
      </Flex>

      <Flex className={styles.accountSaveRow}>
        <Button variant="primary" size="md" pressEffect="glow" onClick={onSave} disabled={!canSave}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </Flex>
    </Flex>
  );
}
